package fyi.moko.hotpl.domain.model

import kotlin.random.Random

/**
 * A user's shareable invite code (Phase 3 Social). Possessing the code is the owner's
 * consent to become friends; redeeming it is the redeemer's consent — so a friendship is
 * mutual by construction without a pending-request inbox.
 *
 * [ownerName] rides on the code doc because no per-user profile document exists: it is the
 * only way the redeemer can learn the inviter's display name, and the Firestore rules pin
 * the friendship's stored inviter name to this value.
 */
data class InviteCode(
    val code: String,
    val ownerUid: String,
    val ownerName: String,
    val createdAt: Long,
)

/**
 * A mutual friendship between exactly two users. [uids] is sorted ascending and [id] is
 * [canonicalFriendshipId] of the pair — the same id both sides compute, so a duplicate
 * friendship cannot exist. [names] maps each uid to the display name captured at creation
 * time (frozen; there is no update path without profile docs).
 */
data class Friendship(
    val id: String,
    val uids: List<String>,
    val names: Map<String, String>,
    val inviteCode: String,
    val createdAt: Long,
) {
    fun otherUid(myUid: String): String = if (uids[0] == myUid) uids[1] else uids[0]

    fun toFriend(myUid: String): Friend {
        val other = otherUid(myUid)
        return Friend(
            uid = other,
            displayName = names[other] ?: ANONYMOUS_NAME,
            friendshipId = id,
            since = createdAt,
        )
    }

    companion object {
        const val ANONYMOUS_NAME = "Anonymous"
    }
}

/** The other party of a [Friendship], from the current user's point of view. */
data class Friend(
    val uid: String,
    val displayName: String,
    val friendshipId: String,
    val since: Long,
)

/**
 * A friend's shared place as read from the `/public_places` projection — the PUBLIC/FRIENDS
 * subset of place fields, never the private ones (note, image, cadence...).
 */
data class FriendSharedPlace(
    val placeId: String,
    val ownerId: String,
    val name: String,
    val status: PlaceStatus,
    val liked: Boolean,
    val latitude: Double?,
    val longitude: Double?,
    val tags: List<String>,
    val updatedAt: Long,
    /** Shared photo file names, fetched from `shared/{ownerId}/` — cover first. */
    val photos: List<String> = emptyList(),
    val phoneNumber: String? = null,
    val website: String? = null,
)

/**
 * Canonical friendship doc id: the two uids sorted lexicographically, joined by '_'.
 * Must match `friendshipIdFor()` in `firestore.rules.prod` — uids are ASCII, so Kotlin's
 * UTF-16 comparison and the rules' byte-wise comparison agree.
 */
fun canonicalFriendshipId(a: String, b: String): String =
    if (a < b) "${a}_$b" else "${b}_$a"

/**
 * Mints invite codes. Injected (rather than called inline) so tests can hand out
 * predictable codes — same pattern as [PlaceIdGenerator].
 */
fun interface InviteCodeGenerator {
    fun newCode(): String
}

object RandomInviteCodeGenerator : InviteCodeGenerator {
    /**
     * Ambiguity-free alphabet (no 0/O, 1/I/L): codes get read aloud and retyped. 31 chars ^
     * 8 ≈ 40 bits — not CSPRNG-grade, accepted for MVP because codes cannot be enumerated
     * (the rules deny non-owner list queries) and are revocable.
     */
    const val ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ"
    const val LENGTH = 8

    override fun newCode(): String = buildString(LENGTH) {
        repeat(LENGTH) { append(ALPHABET[Random.nextInt(ALPHABET.length)]) }
    }
}
